import { memo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock3, Monitor, Play } from 'lucide-react';
import { Course } from '@/types';
import { PriceDisplay } from '@/components/public/PriceDisplay';
import { VideoPreviewModal } from '@/components/public/VideoPreviewModal';

interface CourseCardProps {
  course: Course;
}

const formatDuration = (seconds: number) => {
  if (!seconds) return 'Self paced';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
};

const getCourseCategory = (course: Course) => {
  const text = `${course.title} ${course.description}`.toLowerCase();
  if (text.includes('saving') || text.includes('kids') || text.includes('entrepreneur')) return 'Personal Finance';
  if (text.includes('finance')) return 'Finance';
  return course.audience?.[0] || course.level;
};

const getCardTone = (course: Course) => {
  const category = getCourseCategory(course).toLowerCase();
  return category.includes('personal') ? 'olive' : 'blue';
};

export const CourseCard = memo(({ course }: CourseCardProps) => {
  const navigate = useNavigate();
  const [hasImageError, setHasImageError] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const durationLabel = formatDuration(course.totalDuration);
  const modeLabel = course.teachingMode || 'Guided';
  const shouldShowImage = Boolean(course.thumbnailUrl && !hasImageError);
  const tone = getCardTone(course);
  const priceLabel = course.price != null && course.price > 0 ? `INR ${course.price.toLocaleString('en-IN')}` : undefined;
  const detailHref = `/courses/${encodeURIComponent(course.slug)}`;
  const hasVideo = Boolean(course.previewVideoUrl);

  const openCard = () => {
    if (hasVideo) setIsVideoOpen(true);
    else navigate(detailHref);
  };

  return (
    <>
      <article
        className={`course-card course-card--${tone} group cursor-pointer`}
        role="button"
        tabIndex={0}
        aria-label={hasVideo ? `Play video preview for ${course.title}` : `View ${course.title}`}
        onClick={openCard}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openCard();
          }
        }}
      >
        <div className="course-card-media">
          {shouldShowImage ? (
            <img
              src={course.thumbnailUrl}
              alt={course.title}
              className="course-card-image"
              loading="lazy"
              decoding="async"
              width="480"
              height="270"
              onError={() => setHasImageError(true)}
            />
          ) : (
            <div className="course-card-placeholder">
              <BookOpen className="h-7 w-7" />
            </div>
          )}
          {hasVideo ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/35 opacity-100 transition duration-200 sm:opacity-0 sm:group-hover:opacity-100">
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/70 bg-white/95 text-slate-950 shadow-xl">
                <Play className="ml-0.5 h-6 w-6 fill-current" />
              </span>
            </div>
          ) : null}
        </div>

        <div className="course-card-body">
          <h2 className="course-card-title">{course.title}</h2>

          <div className="course-card-meta">
            <span>
              <Clock3 className="h-4 w-4" />
              <span className="course-card-meta-label">{durationLabel}</span>
            </span>
            <span>
              <Monitor className="h-4 w-4" />
              <span className="course-card-meta-label">{modeLabel}</span>
            </span>
          </div>

          <Link
            to={detailHref}
            className="course-card-action"
            onClick={(event) => event.stopPropagation()}
          >
            <PriceDisplay isFree={course.isFree} originalPrice={priceLabel} className="min-w-0" />
            <span className="ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap">
              View course
              <ArrowRight className="course-card-action-arrow h-4 w-4" />
            </span>
          </Link>
        </div>
      </article>

      <VideoPreviewModal
        isOpen={isVideoOpen}
        title={course.title}
        videoUrl={course.previewVideoUrl || ''}
        posterUrl={course.thumbnailUrl}
        detailHref={detailHref}
        detailLabel="View Course"
        onClose={() => setIsVideoOpen(false)}
      />
    </>
  );
});
